/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useEffect, useState, useRef } from "react";
import { useDispatch } from "react-redux";
import Todo from "./Todo";
import PieChartt from "./PieChart";
import Chart from "./Chart";
import DateRangeFilter from "./DateRangeFilter";
import WebsiteHits from "./WebsiteHits";
import AvgLife from "./AvgLife";
import { fetchCoachProfileByEmailId } from "../../redux/Slices/profileSettingsSlice";
import { UnitContext } from "../../hooks/UnitProvider";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { CONSTANTS } from "../../utils/Constant/Constant";
import useCustomTranslation from "../../hooks/useCustomTranslation";
import appInsights from "../../AppInsights";

const Home = ({ logout }) => {
  const { trans } = useCustomTranslation();
  const dispatch = useDispatch();
  const { toggleUnit } = useContext(UnitContext);
  const { clientAnalytics, loading } = useSelector((state) => state.dashboard);
  const { profileData, error } = useSelector((state) => state.profile);

  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    return new Date(date.getFullYear(), date.getMonth(), 1);
  });

  const [endDate, setEndDate] = useState(() => {
    const date = new Date();
    return new Date(date.getFullYear(), date.getMonth() + 1, 0);
  });

  // Refs for time tracking
  const pageLoadTime = useRef(Date.now());
  const timeTrackingInterval = useRef(null);
  const interactionCount = useRef(0);
  const widgetInteractions = useRef(new Set());
  const totalTimeMinutes = useRef(0);

  // Track page view on component mount
  useEffect(() => {
    const openTime = new Date().toLocaleString();
    appInsights.trackPageView({
      name: "Dashboard Home Page",
      properties: {
        openedAt: openTime,
        coachId: profileData?.coachId,
        coachEmail: emailToUse,
        activeClients: clientAnalytics?.activeClients || 0,
      },
    });

    // Track immediate page load
    trackTimeSpentEvent("Dashboard_Page_Loaded", 0);

    // Set up interval time tracking - every 60 seconds
    setupTimeTrackingInterval();

    // Set up visibility change tracking (for when user switches tabs)
    const cleanupVisibility = setupVisibilityTracking();

    // Cleanup function
    return () => {
      cleanupVisibility();
      if (timeTrackingInterval.current) {
        clearInterval(timeTrackingInterval.current);
      }
    };
  }, []);

  // Simple time tracking - check every 60 seconds
  const setupTimeTrackingInterval = () => {
    timeTrackingInterval.current = setInterval(() => {
      totalTimeMinutes.current += 1; // Add 1 minute each interval
      trackTimeSpentEvent("Dashboard_Time_Spent", totalTimeMinutes.current);
    }, 60000); // Every 60 seconds
  };

  // Track when user switches tabs/windows
  const setupVisibilityTracking = () => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Page is hidden (user switched tabs)
        trackTimeSpentEvent("Dashboard_Page_Hidden", totalTimeMinutes.current);
      } else {
        // Page is visible again
        trackTimeSpentEvent("Dashboard_Page_Visible", totalTimeMinutes.current);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Return cleanup function
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  };

  // Helper to get current time spent in minutes
  const getCurrentTimeSpentMinutes = () => {
    const seconds = (Date.now() - pageLoadTime.current) / 1000;
    return Math.floor(seconds / 60);
  };

  // Generic time tracking function
  const trackTimeSpentEvent = (eventName, minutes) => {
    appInsights.trackEvent({
      name: eventName,
      properties: {
        coachId: profileData?.coachId,
        totalTimeMinutes: minutes,
        totalTimeFormatted: `${minutes} minutes`,
        startDate: startDate?.toISOString(),
        endDate: endDate?.toISOString(),
        activeClients: clientAnalytics?.activeClients || 0,
        pageLoadTimestamp: new Date(pageLoadTime.current).toISOString(),
        eventTimestamp: new Date().toISOString(),
        interactionCount: interactionCount.current,
        uniqueWidgetsInteracted: widgetInteractions.current.size,
      },
    });
  };

  // Track total time spent
  const trackTotalTimeSpent = () => {
    const finalMinutes = getCurrentTimeSpentMinutes();
    
    appInsights.trackEvent({
      name: "Dashboard_Total_Time_Spent",
      properties: {
        coachId: profileData?.coachId,
        totalTimeMinutes: finalMinutes,
        totalTimeFormatted: `${finalMinutes} minutes`,
        startDate: startDate?.toISOString(),
        endDate: endDate?.toISOString(),
        activeClients: clientAnalytics?.activeClients || 0,
        totalInteractions: interactionCount.current,
        uniqueWidgetsInteracted: widgetInteractions.current.size,
        widgetsInteracted: Array.from(widgetInteractions.current),
        dateFiltersUsed: !!(startDate && endDate),
        pageLoadTimestamp: new Date(pageLoadTime.current).toISOString(),
        pageExitTimestamp: new Date().toISOString(),
      },
    });
  };

  const handleDateChange = (newStartDate, newEndDate) => {
    // Track date filter changes
    interactionCount.current += 1;
    appInsights.trackEvent({
      name: "Dashboard_Date_Filter_Changed",
      properties: {
        coachId: profileData?.coachId,
        previousStartDate: startDate?.toISOString(),
        previousEndDate: endDate?.toISOString(),
        newStartDate: newStartDate?.toISOString(),
        newEndDate: newEndDate?.toISOString(),
        timeOnPage: `${getCurrentTimeSpentMinutes()} minutes`,
        interactionNumber: interactionCount.current,
      },
    });

    if (newStartDate) setStartDate(newStartDate);
    if (newEndDate) setEndDate(newEndDate);
  };

  const hits = [
    { name: "Mon", value: 200 },
    { name: "Tue", value: 212 },
    { name: "Wed", value: 111 },
    { name: "Thr", value: 90 },
    { name: "Fri", value: 70 },
    { name: "Sat", value: 300 },
    { name: "Sun", value: 400 },
  ];

  const client = [
    { name: profileData?.displayName, value: clientAnalytics?.activeClients },
  ];
  
  // Get both possible email IDs from localStorage
  const coachEmailId = localStorage.getItem("coachemail");
  const emailId = localStorage.getItem("userName");

  // Determine which email to use (coachemail takes priority)
  const emailToUse = coachEmailId || emailId;

  // Fetch profile on component mount
  useEffect(() => {
    if (emailToUse) {
      // Track profile fetch attempt
      appInsights.trackEvent({
        name: "Dashboard_Profile_Fetch_Attempt",
        properties: {
          coachEmail: emailToUse,
          source: coachEmailId ? "coachemail" : "username",
          timeOnPage: `${getCurrentTimeSpentMinutes()} minutes`,
        },
      });

      dispatch(fetchCoachProfileByEmailId({ emailId: emailToUse, toggleUnit }));
    } else {
      // Track missing email scenario
      appInsights.trackEvent({
        name: "Dashboard_Missing_Email",
        properties: {
          coachEmailId: !!coachEmailId,
          emailId: !!emailId,
          timeOnPage: `${getCurrentTimeSpentMinutes()} minutes`,
        },
      });
    }
  }, [dispatch]);

  useEffect(() => {
    if (profileData?.status === "Inactive") {
      // Track total time before logout for inactive coach
      trackTotalTimeSpent();
      
      // Track inactive coach logout
      appInsights.trackEvent({
        name: "Dashboard_Inactive_Coach_Logout",
        properties: {
          coachId: profileData?.coachId,
          coachName: profileData?.displayName,
          status: profileData?.status,
          totalTimeOnDashboard: `${getCurrentTimeSpentMinutes()} minutes`,
          totalInteractions: interactionCount.current,
        },
      });

      logout();
      // Display a success toast notification
      toast.error(
        <div>
          <strong>{trans("Common.inactiveCoach")}</strong>
        </div>,
        {
          position: "top-center",
          zIndex: 9999,
          toastId: "error-toast",
          autoClose: CONSTANTS.TOAST_DURATION_LONG,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
        }
      );
    }
    if (error?.status === 404) {
      // Track total time before logout for 404 error
      trackTotalTimeSpent();
      
      // Track 404 error logout
      appInsights.trackEvent({
        name: "Dashboard_404_Error_Logout",
        properties: {
          coachEmail: emailToUse,
          errorStatus: error?.status,
          errorMessage: error?.data?.message,
          totalTimeOnDashboard: `${getCurrentTimeSpentMinutes()} minutes`,
          totalInteractions: interactionCount.current,
        },
      });

      logout();
      // Display a error toast notification
      toast.error(
        <div>
          <strong>{error?.data?.message}</strong>
        </div>,
        {
          position: "top-center",
          zIndex: 9999,
          toastId: "error-toast",
          autoClose: CONSTANTS.TOAST_DURATION_LONG,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
        }
      );
    }

    // Track successful profile load
    if (profileData && !error) {
      appInsights.trackException({
        exception: new Error("Dashboard_Profile_Load_Success"),
        severityLevel: 0,
        properties: {
          coachId: profileData?.coachId,
          coachName: profileData?.displayName,
          status: profileData?.status,
          loadTime: `${getCurrentTimeSpentMinutes()} minutes`,
        },
      });
    }
  }, [profileData, error]);

  // Track dashboard analytics load
  useEffect(() => {
    if (clientAnalytics) {
      appInsights.trackEvent({
        name: "Dashboard_Analytics_Loaded",
        properties: {
          coachId: profileData?.coachId,
          activeClients: clientAnalytics?.activeClients || 0,
          totalClients: clientAnalytics?.totalClients || 0,
          newClients: clientAnalytics?.newClients || 0,
          loading: loading,
          timeToAnalyticsLoad: `${getCurrentTimeSpentMinutes()} minutes`,
        },
      });
    }
  }, [clientAnalytics, loading]);

  // Track component unmount (page navigation)
  useEffect(() => {
    return () => {
      trackTotalTimeSpent();
      
      // Also track page unload for backward compatibility
      appInsights.trackEvent({
        name: "Dashboard_Page_Unload",
        properties: {
          coachId: profileData?.coachId,
          totalTimeMinutes: getCurrentTimeSpentMinutes(),
          totalTimeFormatted: `${getCurrentTimeSpentMinutes()} minutes`,
          activeClients: clientAnalytics?.activeClients || 0,
          startDate: startDate?.toISOString(),
          endDate: endDate?.toISOString(),
        },
      });
    };
  }, [profileData?.coachId, clientAnalytics?.activeClients, startDate, endDate]);

  // Track widget interactions
  const handleWidgetInteraction = (widgetName, action) => {
    interactionCount.current += 1;
    widgetInteractions.current.add(widgetName);
    
    appInsights.trackEvent({
      name: "Dashboard_Widget_Interaction",
      properties: {
        coachId: profileData?.coachId,
        widgetName: widgetName,
        action: action,
        timestamp: new Date().toISOString(),
        timeOnPage: `${getCurrentTimeSpentMinutes()} minutes`,
        interactionNumber: interactionCount.current,
        totalUniqueWidgets: widgetInteractions.current.size,
        allInteractedWidgets: Array.from(widgetInteractions.current),
      },
    });
  };

  return (
    <div className="home-container">
      {/* Welcome Section */}

      {/* Main Layout */}
      <div className="home-layout">
        {/* Left Column: Todo, Website Hits, Avg Life */}
        <div className="left-column">
          <div 
            className="widget-container"
            onClick={() => handleWidgetInteraction("Todo", "view")}
          >
            <Todo />
          </div>
          <div 
            className="widget-container"
            onClick={() => handleWidgetInteraction("Chart", "view")}
          >
            <Chart startDate={startDate} endDate={endDate} />
          </div>
          <div 
            className="widget-container"
            onClick={() => handleWidgetInteraction("AvgLife", "view")}
          >
            <AvgLife List={client} loadingdata={loading} />
          </div>
        </div>

        {/* Right Column: Date Filter, PieChart, Chart */}
        <div className="right-column">
          <div className="date-filter-container">
            <DateRangeFilter
              startDate={startDate}
              endDate={endDate}
              setStartDate={(date) => handleDateChange(date, null)}
              setEndDate={(date) => handleDateChange(null, date)}
            />
          </div>
          <div 
            className="widget-container"
            onClick={() => handleWidgetInteraction("PieChart", "view")}
          >
            <PieChartt startDate={startDate} endDate={endDate} />
          </div>
          <div 
            className="widget-container"
            onClick={() => handleWidgetInteraction("WebsiteHits", "view")}
          >
            <WebsiteHits data={hits} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;